import React from "react";
import { GridColDef } from "@mui/x-data-grid";
import { Box, Tooltip } from "@mui/material";
import { EditButton, DeleteButton } from "@refinedev/mui";

import { useDateOptions } from "@shared/options";
import { Avatar, TableCell } from "@shared/components";

export const useBooksListColumns = (
  onRefetch: () => void,
  translate: (key: string) => string,
): GridColDef[] => {
  const { getMonthOptions } = useDateOptions();

  const monthOptions = getMonthOptions();

  return React.useMemo<GridColDef[]>(
    () => [
      {
        field: "id",
        headerName: translate("v1/book_admin.fields.id"),
        width: 80,
      },
      {
        field: "cover",
        headerName: translate("v1/book_admin.fields.cover"),
        minWidth: 120,
        headerAlign: "center",
        renderCell: ({ value }) => {
          const imageSrc = value ?? "/images/image-icon-picture-not-available-picturer.jpeg";

          return (
            <TableCell.Container align="center">
              <Tooltip
                arrow
                placement="right"
                slotProps={{
                  tooltip: {
                    sx: {
                      p: 0,
                      maxWidth: "none",
                    },
                  },
                }}
                title={
                  <Box
                    component="img"
                    src={imageSrc}
                    alt="Book cover"
                    sx={{
                      maxWidth: 400,
                      maxHeight: 500,
                      display: "block",
                    }}
                  />
                }
              >
                <Box sx={{ width: 60 }}>
                  <Avatar
                    src={imageSrc}
                    alt="Book cover"
                    shape="square"
                    fullWidth
                  />
                </Box>
              </Tooltip>
            </TableCell.Container>
          );
        },
      },
      {
        field: "title",
        headerName: translate("v1/book_admin.fields.title"),
        minWidth: 250,
        flex: 1,
        headerAlign: "left",
        renderCell: ({ value }) => (
          <TableCell.Text
            value={value}
            textAlign="left"
          />
        ),
      },
      {
        field: "isbn",
        headerName: translate("v1/book_admin.fields.isbn"),
        minWidth: 160,
        headerAlign: "left",
        renderCell: ({ value }) => (
          <TableCell.Text
            value={value}
            textAlign="left"
          />
        ),
      },
      {
        field: "origin",
        headerName: translate("v1/book_admin.fields.origin"),
        minWidth: 120,
        headerAlign: "center",
        renderCell: ({ value }) => (
          <TableCell.Text
            value={value}
            align="center"
          />
        ),
      },
      {
        field: "agreement_name",
        headerName: translate("v1/book_admin.fields.agreement_name"),
        minWidth: 180,
        flex: 1,
        headerAlign: "left",
        renderCell: ({ value }) => (
          <TableCell.Text
            value={value}
            textAlign="left"
          />
        ),
      },
      {
        field: "display_type",
        headerName: translate("v1/book_admin.fields.type"),
        minWidth: 120,
        headerAlign: "center",
        renderCell: ({ value }) => (
          <TableCell.Text
            value={value}
            align="center"
          />
        ),
      },
      {
        field: "month_featured",
        headerName: translate("v1/book_admin.fields.month_featured"),
        minWidth: 150,
        headerAlign: "center",
        renderCell: ({ value, row }) => {
          const hasMonth = typeof row.month_featured !== "undefined" && row.month_featured !== null;
          const hasYear = typeof row.month_featured_year !== "undefined" && row.month_featured_year !== null;

          if (!hasMonth || !hasYear) {
            return (
              <TableCell.Text
                value="-"
                align="center"
              />
            );
          }

          const month = monthOptions.find(m => String(m.value) === String(row.month_featured))?.label;
          const formattedValue = month
            ? `${month}/${row.month_featured_year}`
            : "-";

          return (
            <TableCell.Text
              value={formattedValue}
              align="center"
            />
          );
        },
      },
      {
        field: "actions",
        headerName: translate("table.actions"),
        headerAlign: "center",
        minWidth: 120,
        sortable: false,
        renderCell: ({ row }) => (
          <TableCell.Container align="center">
            <EditButton
              hideText
              recordItemId={row.id}
            />
            <DeleteButton
              hideText
              recordItemId={row.id}
              onSuccess={onRefetch}
            />
          </TableCell.Container>
        ),
      },
    ],
    [translate, onRefetch, monthOptions],
  );
};
